import { useState } from 'react';

type ProductForm = 'tools' | 'subscription' | 'service';

interface ProductFormTabsProps {
  onOpenDrawer?: (source?: string, needTax?: boolean) => void;
}

export default function ProductFormTabs({ onOpenDrawer }: ProductFormTabsProps) {
  const [activeTab, setActiveTab] = useState<ProductForm>('subscription');

  return (
    <section className="bg-gray-50 border-y border-gray-100 py-16">
      <div className="max-w-[1400px] mx-auto px-6">
        {/* 标题 */}
        <div className="mb-10 text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">产品形态与交付能力</h2>
          <p className="text-gray-600">三种产品形态，分别交付什么能力、适合谁、边界是什么</p>
        </div>

        {/* Tab 切换器 */}
        <div className="flex justify-center mb-12">
          <div className="flex items-center gap-1 bg-white border border-gray-200 rounded-full p-1 shadow-sm">
            <button
              onClick={() => setActiveTab('tools')}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'tools'
                  ? 'bg-[#14B8A6] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              A类自动化工具（先跑通）
            </button>
            <button
              onClick={() => setActiveTab('subscription')}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'subscription'
                  ? 'bg-[#14B8A6] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              SaaS订阅版（长期治理）
            </button>
            <button
              onClick={() => setActiveTab('service')}
              className={`px-8 py-3 rounded-full text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                activeTab === 'service'
                  ? 'bg-[#14B8A6] text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              服务包（增强交付）
            </button>
          </div>
        </div>

        {/* Tab 内容 */}
        {activeTab === 'tools' && <ToolsContent onOpenDrawer={onOpenDrawer} />}
        {activeTab === 'subscription' && <SubscriptionContent onOpenDrawer={onOpenDrawer} />}
        {activeTab === 'service' && <ServiceContent onOpenDrawer={onOpenDrawer} />}
      </div>
    </section>
  );
}

// A类自动化工具内容
function ToolsContent({ onOpenDrawer }: { onOpenDrawer?: (source?: string, needTax?: boolean) => void }) {
  return (
    <div>
      {/* 标题 */}
      <div className="text-center mb-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">A类自动化工具｜先跑通一票，快速验证闭环</h3>
      </div>

      <div className="grid grid-cols-12 gap-8 mb-10">
        {/* 左侧：适合谁 */}
        <div className="col-span-4">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 h-full">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#14B8A6]/10">
                <i className="ri-user-star-line text-[#14B8A6] text-xl"></i>
              </div>
              <span className="text-lg font-semibold text-gray-900">适合谁</span>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">新手起步</div>
                  <div className="text-sm text-gray-600">先把资料清单与回执流程跑通</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">低频需求</div>
                  <div className="text-sm text-gray-600">按票处理，不想上年费系统</div>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">团队验证</div>
                  <div className="text-sm text-gray-600">内部先形成"正确流程模板"</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：核心能力 */}
        <div className="col-span-8">
          <div className="grid grid-cols-3 gap-6">
            {/* 能力卡1 */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#14B8A6]/10 mb-4">
                <i className="ri-file-text-line text-[#14B8A6] text-2xl"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">单证自动生成</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                订仓单/草单等自动生成，减少手工录入与错填
              </p>
            </div>

            {/* 能力卡2 */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#14B8A6]/10 mb-4">
                <i className="ri-shield-check-line text-[#14B8A6] text-2xl"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">提交前校验</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                缺项/异常提前提示，不通过则退回补齐后重提
              </p>
            </div>

            {/* 能力卡3 */}
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
              <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-[#14B8A6]/10 mb-4">
                <i className="ri-send-plane-line text-[#14B8A6] text-2xl"></i>
              </div>
              <h4 className="text-base font-semibold text-gray-900 mb-2">直连提交与回执回传</h4>
              <p className="text-sm text-gray-600 leading-relaxed">
                提交记录与回执状态回传，便于追踪与复盘
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 爆款体验 */}
      <div className="grid grid-cols-2 gap-6 mb-8">
        <div className="bg-gradient-to-br from-[#14B8A6]/5 to-[#14B8A6]/10 rounded-xl border border-[#14B8A6]/20 p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-3xl font-bold text-[#14B8A6] mb-2">¥9.9</div>
              <div className="text-lg font-semibold text-gray-900 mb-1">跑通体验</div>
              <div className="text-sm text-gray-600">用于验证资料结构与流程输出</div>
            </div>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14B8A6]/10">
              <i className="ri-flashlight-line text-[#14B8A6] text-xl"></i>
            </div>
          </div>
          <button
            onClick={() => onOpenDrawer?.('A类工具-9.9体验', false)}
            className="w-full bg-[#14B8A6] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#12a594] transition-colors whitespace-nowrap cursor-pointer"
          >
            立即体验
          </button>
        </div>

        <div className="bg-gradient-to-br from-[#14B8A6]/5 to-[#14B8A6]/10 rounded-xl border border-[#14B8A6]/20 p-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="text-3xl font-bold text-[#14B8A6] mb-2">¥99</div>
              <div className="text-lg font-semibold text-gray-900 mb-1">闭环体验</div>
              <div className="text-sm text-gray-600">包含一次完整链路（以实际商品口径为准）</div>
            </div>
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#14B8A6]/10">
              <i className="ri-loop-right-line text-[#14B8A6] text-xl"></i>
            </div>
          </div>
          <button
            onClick={() => onOpenDrawer?.('A类工具-99闭环', false)}
            className="w-full bg-[#14B8A6] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#12a594] transition-colors whitespace-nowrap cursor-pointer"
          >
            立即体验
          </button>
        </div>
      </div>

      {/* 边界提示 */}
      <div className="bg-gray-100 rounded-xl p-6 mb-8">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-gray-500 text-xl mt-0.5 flex-shrink-0"></i>
          <div className="space-y-2 text-sm text-gray-700">
            <div>• 零人工介入；校验不通过退回补齐后重提</div>
            <div>• 不承诺放行/审批结果</div>
            <div>• 单证默认留存策略以产品说明为准</div>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => onOpenDrawer?.('A类工具', false)}
          className="bg-[#14B8A6] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#12a594] transition-colors whitespace-nowrap cursor-pointer"
        >
          先跑通一票（9.9/99）
        </button>
        <button
          onClick={() => {
            const element = document.getElementById('version-recommender');
            element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="bg-white text-gray-700 px-8 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          查看订阅版（适合长期治理）
        </button>
      </div>
    </div>
  );
}

// SaaS订阅版内容
function SubscriptionContent({ onOpenDrawer }: { onOpenDrawer?: (source?: string, needTax?: boolean) => void }) {
  return (
    <div>
      {/* 标题 */}
      <div className="text-center mb-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">
          SaaS订阅版｜把合规做成管理能力（可解释、可追溯、可复盘）
        </h3>
      </div>

      <div className="grid grid-cols-12 gap-8 mb-10">
        {/* 左侧：治理能力 */}
        <div className="col-span-7">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#14B8A6]/10">
                <i className="ri-shield-star-line text-[#14B8A6] text-xl"></i>
              </div>
              <span className="text-lg font-semibold text-gray-900">你获得的治理能力</span>
            </div>
            <div className="space-y-4">
              <div className="flex items-start gap-3 bg-[#14B8A6]/5 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">资料归集</div>
                  <div className="text-sm text-gray-600">按主体/批次/票号统一组织数据与材料</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-[#14B8A6]/5 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">规则校验</div>
                  <div className="text-sm text-gray-600">提交前缺项/异常/逻辑校验，降低返工</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-[#14B8A6]/5 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">差异留痕</div>
                  <div className="text-sm text-gray-600">关键差异标记与处理记录可追溯</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-[#14B8A6]/5 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">单证归档</div>
                  <div className="text-sm text-gray-600">目录化归档、可下载导出（按版本留存策略）</div>
                </div>
              </div>
              <div className="flex items-start gap-3 bg-[#14B8A6]/5 rounded-lg p-4">
                <i className="ri-checkbox-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
                <div>
                  <div className="font-medium text-gray-900 mb-1">链路管理</div>
                  <div className="text-sm text-gray-600">业务关系结构化沉淀（支持核销/复盘）</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 右侧：交付物清单 */}
        <div className="col-span-5">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8 h-full">
            <div className="flex items-center gap-2 mb-6">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-[#14B8A6]/10">
                <i className="ri-file-list-3-line text-[#14B8A6] text-xl"></i>
              </div>
              <span className="text-lg font-semibold text-gray-900">交付物清单</span>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3 border-l-4 border-[#14B8A6] pl-4 py-2">
                <i className="ri-file-text-line text-gray-400 text-base"></i>
                <span className="text-sm text-gray-700">缺资料清单（按批次/票号）</span>
              </div>
              <div className="flex items-center gap-3 border-l-4 border-[#14B8A6] pl-4 py-2">
                <i className="ri-file-text-line text-gray-400 text-base"></i>
                <span className="text-sm text-gray-700">校验报告（提交前）</span>
              </div>
              <div className="flex items-center gap-3 border-l-4 border-[#14B8A6] pl-4 py-2">
                <i className="ri-file-text-line text-gray-400 text-base"></i>
                <span className="text-sm text-gray-700">差异留痕记录（可追溯）</span>
              </div>
              <div className="flex items-center gap-3 border-l-4 border-[#14B8A6] pl-4 py-2">
                <i className="ri-file-text-line text-gray-400 text-base"></i>
                <span className="text-sm text-gray-700">归档包目录（可导出）</span>
              </div>
              <div className="flex items-center gap-3 border-l-4 border-[#14B8A6] pl-4 py-2">
                <i className="ri-file-text-line text-gray-400 text-base"></i>
                <span className="text-sm text-gray-700">回执/退单原因记录（如有）</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 适合谁 */}
      <div className="bg-gradient-to-br from-[#14B8A6]/5 to-[#14B8A6]/10 rounded-xl border border-[#14B8A6]/20 p-8 mb-8">
        <div className="flex items-center gap-2 mb-4">
          <i className="ri-user-star-line text-[#14B8A6] text-xl"></i>
          <span className="text-lg font-semibold text-gray-900">适合谁</span>
        </div>
        <div className="grid grid-cols-3 gap-6">
          <div className="flex items-start gap-3">
            <i className="ri-arrow-right-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
            <span className="text-sm text-gray-700">稳定出货、规模增长、需要制度化管理</span>
          </div>
          <div className="flex items-start gap-3">
            <i className="ri-arrow-right-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
            <span className="text-sm text-gray-700">多主体、多部门协作，需要统一口径</span>
          </div>
          <div className="flex items-start gap-3">
            <i className="ri-arrow-right-circle-fill text-[#14B8A6] text-lg mt-0.5 flex-shrink-0"></i>
            <span className="text-sm text-gray-700">有核销/审计准备诉求，希望资料结构稳定</span>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className="flex items-center justify-center gap-4">
        <button
          onClick={() => {
            const element = document.getElementById('version-recommender');
            element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }}
          className="bg-[#14B8A6] text-white px-8 py-3 rounded-lg font-medium hover:bg-[#12a594] transition-colors whitespace-nowrap cursor-pointer"
        >
          查看订阅版本与起步价
        </button>
        <button
          onClick={() => onOpenDrawer?.('订阅版咨询', false)}
          className="bg-white text-gray-700 px-8 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
        >
          咨询合规治理方案
        </button>
      </div>
    </div>
  );
}

// 服务包内容
function ServiceContent({ onOpenDrawer }: { onOpenDrawer?: (source?: string, needTax?: boolean) => void }) {
  return (
    <div>
      {/* 标题 */}
      <div className="text-center mb-10">
        <h3 className="text-2xl font-bold text-gray-900 mb-2">服务包｜在治理体系上增强交付（叠加项）</h3>
      </div>

      {/* 顶部原则 */}
      <div className="bg-gray-100 rounded-xl p-6 mb-10">
        <div className="flex items-start gap-3">
          <i className="ri-information-line text-gray-500 text-xl mt-0.5 flex-shrink-0"></i>
          <p className="text-sm text-gray-700">
            服务包为叠加项，不替代订阅费/免税费用；具体交付范围以合同约定为准。
          </p>
        </div>
      </div>

      {/* 服务包卡片 */}
      <div className="grid grid-cols-3 gap-8 mb-10">
        {/* 退税服务包 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#14B8A6]/10 mb-6">
            <i className="ri-refund-2-line text-[#14B8A6] text-2xl"></i>
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-4">退税服务包</h4>

          <div className="space-y-4 mb-6">
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">解决什么</div>
              <div className="text-sm text-gray-700">资料准备与流程推进效率</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">交付物</div>
              <div className="text-sm text-gray-700">材料清单建议、关键材料审核、节点跟踪（按约定）</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">适合谁</div>
              <div className="text-sm text-gray-700">有退税诉求、希望减少返工的企业</div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="text-sm text-gray-500 mb-2">起步价</div>
            <div className="text-3xl font-bold text-[#14B8A6]">¥7,500<span className="text-base font-normal text-gray-500">/年起</span></div>
          </div>

          <button
            onClick={() => onOpenDrawer?.('退税服务包', true)}
            className="w-full bg-[#14B8A6] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#12a594] transition-colors whitespace-nowrap cursor-pointer"
          >
            获取报价单
          </button>
        </div>

        {/* 核算服务包 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#14B8A6]/10 mb-6">
            <i className="ri-calculator-line text-[#14B8A6] text-2xl"></i>
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-4">核算服务包</h4>

          <div className="space-y-4 mb-6">
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">解决什么</div>
              <div className="text-sm text-gray-700">账务口径统一、成本/税费归集更清晰</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">交付物</div>
              <div className="text-sm text-gray-700">口径建议、对账清单/台账（按约定）</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">适合谁</div>
              <div className="text-sm text-gray-700">利润核算压力大、多主体核算</div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="text-sm text-gray-500 mb-2">价格</div>
            <div className="text-lg font-medium text-gray-700">咨询获取方案</div>
          </div>

          <button
            onClick={() => onOpenDrawer?.('核算服务包', false)}
            className="w-full bg-white text-gray-700 px-6 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            咨询方案
          </button>
        </div>

        {/* 值守保障 */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-8">
          <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#14B8A6]/10 mb-6">
            <i className="ri-customer-service-2-line text-[#14B8A6] text-2xl"></i>
          </div>
          <h4 className="text-xl font-bold text-gray-900 mb-4">值守保障</h4>

          <div className="space-y-4 mb-6">
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">解决什么</div>
              <div className="text-sm text-gray-700">高峰期/夜间周末流程保障</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">交付物</div>
              <div className="text-sm text-gray-700">响应与处理范围（按约定，不含线下跑腿）</div>
            </div>
            <div>
              <div className="text-xs font-medium text-gray-500 mb-2">适合谁</div>
              <div className="text-sm text-gray-700">高频出货、对时效敏感</div>
            </div>
          </div>

          <div className="border-t border-gray-200 pt-6 mb-6">
            <div className="text-sm text-gray-500 mb-2">价格</div>
            <div className="text-lg font-medium text-gray-700">咨询获取方案</div>
          </div>

          <button
            onClick={() => onOpenDrawer?.('值守保障', false)}
            className="w-full bg-white text-gray-700 px-6 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
          >
            咨询方案
          </button>
        </div>
      </div>
    </div>
  );
}